/* globals window */

import React, { useState, useEffect } from 'react';
import { useDataProvider } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import Notifications from '@material-ui/icons/Notifications';
import Badge from '@material-ui/core/Badge';
import Popover from '@material-ui/core/Popover';
import Chip from '@material-ui/core/Chip';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import DOMPurify from 'dompurify';
import { apiGetAsync, apiPut, ResponseStatus } from '../brainApi';
import { addNotificationAction } from '../actions/notificationsAction';
import { addUnreadBooleanAction } from '../actions/unreadMessageAction';

const useStyles = makeStyles(() => ({
  popOver: {
    margin: '15px',
  },
  popoverContent: {
    padding: '10px',
  },
  bellArea: {
    position: 'relative',
    padding: 0,
    '&:hover': {
      cursor: 'pointer',
    },
  },
}));

const NotificationPanel = () => {
  const classes = useStyles();

  const dataProvider = useDataProvider();
  const dispatch = useDispatch();
  const [notifications, setNotifications] = useState([]);
  // Get "old" notification ids: ones that have already been retrieved by this function
  const oldNotifications = useSelector((state) => state.notifications) || {};
  // Element for popover to be anchored to
  const [anchorElement, setAnchorElement] = useState(null);
  const location = useLocation();

  const handleBellClick = (event) => {
    setAnchorElement(event.currentTarget);
    // Mark notifications as read
    apiPut('/event-notification', {
      notifications: notifications.map((notif) => ({
        ...notif,
        is_read: true,
      })),
    });
  };

  const handlePopoverClose = () => {
    setAnchorElement(null);
    setNotifications([]);
  };
  const getNotifications = () => {
    dataProvider
      .getList('event-notification', {
        filter: {
          delivery_method: 'admin',
        },
        pagination: { page: 1, perPage: 10 },
        sort: { field: 'created_at', order: 'DESC' },
      })
      .then(({ data }) => {
        const unreadNotifications = data.filter((notif) => !notif.is_read);
        unreadNotifications
          .filter((notif) => !oldNotifications[notif.id])
          .forEach((notif) => {
            const {
              event_record: { event_type },
            } = notif;
            const browserNotificationsSupported =
              typeof window.Notification !== 'undefined';
            if (browserNotificationsSupported)
              new Notification(event_type.name, {
                body: event_type.short_description,
              });
            // Add the newly fetched notification into the redux-store
            dispatch(addNotificationAction(notif));
          });
        setNotifications(unreadNotifications);
      });
  };

  const getUnreadMessageBoolean = async () => {
    const resp = await apiGetAsync('/get-unread-message-boolean');
    // TODO: Ideally this would be something like resp.Status === ResponseStatus.OK (or better yet, it uses the status code),
    // but we don't have standardized response status' so this is basically saying,
    // if the fetch() itself doesn't fail then dispatch
    if (resp.Status !== ResponseStatus.ERROR)
      dispatch(addUnreadBooleanAction(resp.has_unread_message));
  };

  useEffect(() => {
    getNotifications();
    getUnreadMessageBoolean();
    const interval = setInterval(() => {
      getNotifications();
      getUnreadMessageBoolean();
    }, 60000);
    return () => clearInterval(interval);
  }, [oldNotifications]);

  return (
    <>
      <div className={classes.bellArea}>
        <Badge
          badgeContent={notifications.length <= 9 ? notifications.length : '9+'}
          overlap="circle"
          color="error"
        >
          <Notifications onClick={(event) => handleBellClick(event)} />
        </Badge>
      </div>
      <Popover
        className={classes.popover}
        open={!!anchorElement}
        anchorEl={anchorElement}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Paper className={classes.popoverContent}>
          <Grid
            container
            direction="row"
            justify="space-between"
            alignItems="center"
            style={{ marginBottom: 5 }}
            spacing={1}
          >
            <Grid item>
              <Typography variant="h6">Notifications</Typography>
            </Grid>
            <Grid item>
              <Chip label={`${notifications.length} new`} />
            </Grid>
          </Grid>
          <Divider />
          {notifications.length === 0 && (
            <Typography>No New Notifications</Typography>
          )}
          {notifications.map((notif) => (
            <>
              <Typography
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(notif.content),
                }}
              />
              <Divider />
            </>
          ))}
          <Typography align="center">
            <Link
              to="/activity-feed/"
              onClick={handlePopoverClose}
              replace={location.pathname === '/activity-feed/'}
            >
              See all
            </Link>
          </Typography>
        </Paper>
      </Popover>
    </>
  );
};

export default NotificationPanel;
